import React from "react";
import { Upload } from "lucide-react";

interface FileUploadFieldProps {
  name: string;
  label: string;
  isPicture?: boolean;
  value?: File | null;
  onChange: (value: File | null) => void;
  error?: string;
  currentUrl?: string | null;
}

const FileUploadField = ({
  name,
  label,
  isPicture,
  value,
  onChange,
  error,
  currentUrl,
}: FileUploadFieldProps) => {
  const [fileName, setFileName] = React.useState<string>("");
  const [preview, setPreview] = React.useState<string>("");

  // Keeping a limit of 5MB for image uploads
  const MAX_FILE_SIZE_MB = 5;
  const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (file && file.size > MAX_FILE_SIZE) {
      setPreview("");
      setFileName("");
      onChange(null);
      alert(`File size should be less than ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    setFileName(file ? file.name : "");
    onChange(file);

    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview("");
    }
  };

  return (
    <div className="flex flex-col gap-1 w-full">
      <label className="text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="relative">
        <input
          type="file"
          id={`file-${name}`}
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
        <label
          htmlFor={`file-${name}`}
          className={`flex items-center px-4 py-2 w-full border rounded-lg cursor-pointer transition-colors duration-200
            ${error ? "border-red-500" : ""}
          `}
        >
          <div className="flex items-center gap-2 w-full min-h-[2.5rem]">
            <Upload className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="text-sm text-gray-600 truncate">
              {fileName || "Choose file"}
            </span>
          </div>
          {preview && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <img
                src={preview}
                alt="Preview"
                className="w-8 h-8 object-cover rounded"
              />
            </div>
          )}
        </label>
      </div>
      {error && <span className="text-xs text-red-500 mt-1">{error}</span>}
      {!fileName && currentUrl && (
        <a
          href={currentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-600 underline truncate block mt-1"
        >
          View current file
        </a>
      )}
    </div>
  );
};

export default FileUploadField;
